import { Controller } from '@hotwired/stimulus'
import {
  showMessage,
  showEditForm,
  hideEditForm,
  updateKeywordText
} from '../keyword-dom-manager'
import {
  showAddKeywordLoading,
  hideAddKeywordLoading,
  showProductSwitchLoading,
  hideProductSwitchLoading
} from '../keyword-loading-state'
import {
  validateKeywordInput,
  initializeValidationState,
  clearKeywordInput
} from '../keyword-validation'

/**
 * Keyword Controller
 * 
 * Manages the keyword management interface for SEO keywords on products and product lines.
 * Follows SRP - only handles Stimulus lifecycle and event delegation.
 * Business logic is delegated to helper modules.
 * 
 * Usage:
 * <div data-controller="keyword">
 *   <form data-keyword-target="addForm" data-action="turbo:submit-start->keyword#handleFormSubmitStart turbo:submit-end->keyword#handleFormSubmitEnd">
 *     <input data-keyword-target="input" data-action="keyup->keyword#validateInput keydown->keyword#preventEnterKey">
 *     <button data-keyword-target="addButton">Add</button>
 *   </form>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'addForm',
    'productSelectForm',
    'input',
    'addButton',
    'minCharMessage',
    'addingMessage',
    'addLoadingIcon',
    'switchLoadingIcon',
    'productSelect',
    'messageText',
    'keywordTitle',
    'keywordList',
    'messageData'
  ]

  connect() {
    this.initializeValidation()
    this.observeMessageData()
    this.boundHandleTurboStreamRender = this.handleTurboStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundHandleTurboStreamRender)
  }

  disconnect() {
    if (this.messageObserver) {
      this.messageObserver.disconnect()
    }
    document.removeEventListener('turbo:before-stream-render', this.boundHandleTurboStreamRender)
  }

  /**
   * Handles Turbo Stream render events to process message data
   * @param {CustomEvent} event
   */
  handleTurboStreamRender(event) {
    const stream = event.target
    if (stream.target === 'keyword-message-data') {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const messageElement = this.messageDataTarget?.querySelector('[data-message]')
        if (messageElement) {
          this.processMessage(messageElement)
        }
      })
    }
  }

  /**
   * Observes the messageData target for Turbo Stream updates
   * When content changes, processes the message data
   */
  observeMessageData() {
    if (!this.hasMessageDataTarget) return

    this.messageObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const messageElement = this.messageDataTarget.querySelector('[data-message]')
          if (messageElement) {
            this.processMessage(messageElement)
          }
        }
      })
    })

    this.messageObserver.observe(this.messageDataTarget, {
      childList: true,
      subtree: true
    })
  }

  /**
   * Processes message data from Turbo Stream response
   * @param {HTMLElement} messageElement - Element containing message data attributes
   */
  processMessage(messageElement) {
    const message = messageElement.dataset.message
    const isSuccess = messageElement.dataset.success === 'true'
    const shouldClear = messageElement.dataset.clearInput === 'true'
    const keywordIdentifier = messageElement.dataset.keywordIdentifier
    const keywordText = messageElement.dataset.keywordText

    if (message && this.hasMessageTextTarget) {
      showMessage(this.messageTextTarget, message, isSuccess)
    }

    if (shouldClear) {
      this.clearInput()
    }

    if (keywordIdentifier && keywordText) {
      updateKeywordText(keywordIdentifier, keywordText)
      hideEditForm(keywordIdentifier)
    }
  }

  /**
   * Initialize validation state on connect
   */
  initializeValidation() {
    const buttons = this.hasAddButtonTarget ? this.addButtonTargets : []
    const minCharMessage = this.hasMinCharMessageTarget ? this.minCharMessageTarget : null
    initializeValidationState(buttons, minCharMessage)
  }

  /**
   * Handles messages from Turbo Stream responses
   * @param {CustomEvent} event
   */
  handleMessage(event) {
    const messageElement = event.target
    const message = messageElement.dataset.message
    const isSuccess = messageElement.dataset.success === 'true'
    const shouldClear = messageElement.dataset.clearInput === 'true'
    const keywordIdentifier = messageElement.dataset.keywordIdentifier
    const keywordText = messageElement.dataset.keywordText

    if (message && this.hasMessageTextTarget) {
      showMessage(this.messageTextTarget, message, isSuccess)
    }

    if (shouldClear) {
      this.clearInput()
    }

    if (keywordIdentifier && keywordText) {
      updateKeywordText(keywordIdentifier, keywordText)
      hideEditForm(keywordIdentifier)
    }
  }

  /**
   * Validates keyword input on keyup
   */
  validateInput() {
    if (!this.hasInputTarget) return

    const buttons = this.hasAddButtonTarget ? this.addButtonTargets : []
    const minCharMessage = this.hasMinCharMessageTarget ? this.minCharMessageTarget : null
    validateKeywordInput(this.inputTarget, buttons, minCharMessage)
  }

  /**
   * Prevents form submission on Enter key for keyword fields
   * @param {KeyboardEvent} event
   */
  preventEnterKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  /**
   * Handles form submission start (loading state)
   */
  handleFormSubmitStart() {
    showAddKeywordLoading({
      buttons: this.hasAddButtonTarget ? this.addButtonTargets : [],
      message: this.hasAddingMessageTarget ? this.addingMessageTarget : null,
      loadingIcon: this.hasAddLoadingIconTarget ? this.addLoadingIconTarget : null,
      productSelect: this.hasProductSelectTarget ? this.productSelectTarget : null
    })
  }

  /**
   * Handles form submission end (hide loading, re-validate)
   */
  handleFormSubmitEnd() {
    hideAddKeywordLoading({
      buttons: this.hasAddButtonTarget ? this.addButtonTargets : [],
      message: this.hasAddingMessageTarget ? this.addingMessageTarget : null,
      loadingIcon: this.hasAddLoadingIconTarget ? this.addLoadingIconTarget : null,
      productSelect: this.hasProductSelectTarget ? this.productSelectTarget : null
    })
    this.initializeValidation()
  }

  /**
   * Handles product select form submission start
   */
  handleProductSelectStart() {
    showProductSwitchLoading({
      title: this.hasKeywordTitleTarget ? this.keywordTitleTarget : null,
      loadingIcon: this.hasSwitchLoadingIconTarget ? this.switchLoadingIconTarget : null,
      list: this.hasKeywordListTarget ? this.keywordListTarget : null
    })
  }

  /**
   * Handles product select form submission end
   */
  handleProductSelectEnd() {
    hideProductSwitchLoading({
      loadingIcon: this.hasSwitchLoadingIconTarget ? this.switchLoadingIconTarget : null,
      list: this.hasKeywordListTarget ? this.keywordListTarget : null
    })
    this.initializeValidation()
  }

  /**
   * Submits the product select form
   */
  submitProductSelect() {
    if (this.hasProductSelectFormTarget) {
      this.productSelectFormTarget.requestSubmit()
    }
  }

  /**
   * Shows edit form for a keyword
   * @param {Event} event
   */
  edit(event) {
    event.preventDefault()
    const keywordId = event.currentTarget.dataset.keywordId
    if (keywordId) {
      showEditForm(keywordId)
    }
  }

  /**
   * Cancels keyword edit and hides the form
   * @param {Event} event
   */
  cancelEdit(event) {
    event.preventDefault()
    const keywordId = event.currentTarget.dataset.keywordId
    if (keywordId) {
      hideEditForm(keywordId)
    }
  }

  /**
   * Clears the keyword input after successful add
   */
  clearInput() {
    if (this.hasInputTarget) {
      clearKeywordInput(this.inputTarget)
      this.initializeValidation()
    }
  }
}
